import { forwardRef, Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import { GetDefinitionsGuard } from 'app/core/store/definitions/definitions.resolve';
import { EditRelocationDetail } from 'app/core/store/edit-relocation-detail/relocation-detail.interface';
import { AuthorizationGuard } from 'app/core/store/session/authorization/authorization.resolve';
import { Observable, of } from 'rxjs';
import * as fromRoot from '..';
import { TemplateRestService } from '../../services/rest/template.rest.service';
import * as actions from './template.actions';
import { logger } from 'app/core/util/logger';
import { first, switchMap } from 'rxjs/operators';

/**
 * Guard to async load a template before following a route
 */
@Injectable({ providedIn: 'root' })
export class EditRelocationTemplateGuard implements CanActivate, DependencyTreeCanActivate {
  private templateRestService: TemplateRestService;
  private store: Store<fromRoot.RootState>;

  constructor(@Inject(forwardRef(() => Store)) store, @Inject(forwardRef(() => TemplateRestService)) templateRestService) {
    this.store = store;
    this.templateRestService = templateRestService;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    logger.debug('In EditRelocationTemplateGuard');

    return this.store.select('editRelocationDetail').pipe(
      first(),
      switchMap((editRelocationDetail: EditRelocationDetail) => {
        if (editRelocationDetail.relocationDetail.templateId === +route.paramMap.get('templateId')) {
          // Template already loaded (coming from calendar)
          this.triggerCalculateTruckLoad(editRelocationDetail.relocationDetail.materials[0]);
          return of(true);
        } // else, page refresh, entered portal with url for editing template, first load the template
        return this.templateRestService.getTemplate('RELOCATION', parseInt(route.paramMap.get('templateId'), 10)).pipe(
          switchMap(template => {
            const templateData = template.data;
            this.store.dispatch(new actions.RelocationTemplateEditAction({ template }));
            // TODO: fix this.  used to be lineItem, but now it needs to be called lineItems according to interface.
            // since templates are stored as is, is it correct in new templates, wrong in old ones?
            // next line fixes the issue with a patch
            const lineItems = templateData.lineItems || templateData['lineItem'];
            this.triggerCalculateTruckLoad(lineItems[0]);
            return of(true);
          })
        );
      })
    );
  }

  getDependencies(): any {
    return [AuthorizationGuard, GetDefinitionsGuard];
  }

  getDependencyRouteData(): any {
    return {
      authorization: 'canRelocate',
    };
  }

  private triggerCalculateTruckLoad(lineItem) {
    // TODO what to do with this ???
    // this.store.dispatch(new orderActions.OrderDetailCreateSaveMaterialStart({ material: lineItem, preventRestCall: false, showPopup: false }));
  }
}
